import {Component, OnDestroy, OnInit} from '@angular/core';
import {Update} from '@ngrx/entity';
import {Observable, Subscription} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {SubheaderService} from '../../../../core/_base/layout';
import {AppState} from '../../../../core/reducers';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {delay} from 'rxjs/operators';
import {UnitAreaMeasurementUpdated} from '../../../../core/linked-tables';
import { TranslateService } from '@ngx-translate/core';
import {combineLatest} from 'rxjs'

import {
    UnitAreaMeasurement,
    UnitAreaMeasurementOnServerCreated,
    selectUnitAreaMeasurementsActionLoading,
    selectUnitAreaMeasurementById,
    selectLastCreatedUnitAreaMeasurementId
} from '../../../../core/linked-tables';
import {Location} from '@angular/common';

@Component({
    selector: 'kt-unit-area-measurement-edit',
    templateUrl: './unit-area-measurement-edit.component.html',
    styleUrls: ['./unit-area-measurement-edit.component.scss'],
})


export class UnitAreaMeasurementEditComponent implements OnInit, OnDestroy {

    model: UnitAreaMeasurement;
    oldModel: UnitAreaMeasurement;

    unitAreaMeasurementForm: UntypedFormGroup;
    hasFormErrors = false;

    loading$: Observable<boolean>;
    viewLoading = false;
    loadingAfterSubmit = false;
    mode = 'NEW';
    // Private properties
    private componentSubscriptions: Subscription;
    private subscriptions: Subscription[] = [];

    readonly = false

    /**
     * Component constructor
     *
     * @param subheaderService: SubheaderService
     * @param activatedRoute: ActivatedRoute
     * @param router: Router
     * @param fb: FormBuilder
     * @param location: Location
     * @param store: Store<AppState>
     */
    constructor(public subheaderService: SubheaderService,
                private activatedRoute: ActivatedRoute,
                private router: Router,
                private fb: UntypedFormBuilder,
                private location: Location,
                private store: Store<AppState>,
                private translate: TranslateService) {
    }

    ngOnInit() {
        this.loading$ = this.store.pipe(select(selectUnitAreaMeasurementsActionLoading));
        const routeSubscription = combineLatest([
            this.activatedRoute.params,
            this.activatedRoute.data
        ]).subscribe(([params, data]) => {
            const id = params.id;
            this.readonly = data.readonly
            if (id && id > 0) {
                this.store.pipe(select(selectUnitAreaMeasurementById(id))).subscribe(res => {
                    if (res) {
                        this.oldModel = Object.assign({}, res);
                        this.model = Object.assign({}, this.oldModel);
                        this.initUnitAreaMeasurement();
                    }
                });
            } else {
                this.model = new UnitAreaMeasurement();
                this.model.clear();
                this.oldModel = Object.assign({}, this.model);
                this.initUnitAreaMeasurement();
            }
        });
        this.subscriptions.push(routeSubscription);

        if (this.activatedRoute.snapshot.paramMap.get('id') != null) {
            this.setBreadCrumbs(Number(this.activatedRoute.snapshot.paramMap.get('id')));
        } else {
            this.setBreadCrumbs(0);
        }
    }

    setBreadCrumbs(id: number): void {
        this.subheaderService.setTitle(this.getComponentTitle());
        this.subheaderService.setBreadcrumbs([
            {title: 'Developer', page: `../admin-management`},
            {title: 'Linked tables', page: `../admin-management/linked-tables`},
            {title: 'Interaction types', page: `../admin-management/linked-tables/unit-measurements`},
            {
                title: this.getComponentTitle(),
                page: id > 0 ? `../admin-management/linked-tables/unit-measurements/edit/${id}`
                    : `../admin-management/linked-tables/unit-measurements/add`
            }
        ]);
    }

    ngOnDestroy(): void {
        if (this.componentSubscriptions) {
            this.componentSubscriptions.unsubscribe();
        }
    }

    initUnitAreaMeasurement() {
        this.createForm();
    }


    createForm() {
        this.unitAreaMeasurementForm = this.fb.group({
            name: [this.model.name, Validators.required],
            acronym: [this.model.acronym, Validators.required],
        });
    }

    /**
     * Returns page title
     */
    getComponentTitle(): string {
        if (this.model && this.model.id > 0) {
            this.mode = 'EDIT';
            return this.translate.instant('UNIT_AREA_MEASUREMENT.FORM.TITLE.EDIT', {name: this.model.name});
        }
        return this.translate.instant('UNIT_AREA_MEASUREMENT.FORM.TITLE.NEW');
    }

    /**
     * Check control is invalid
     * @param controlName: string
     */
    isControlInvalid(controlName: string): boolean {
        const control = this.unitAreaMeasurementForm.controls[controlName];
        return control.invalid && control.touched;
    }

    reset() {
        this.model = Object.assign({}, this.oldModel);
        this.createForm();
        this.hasFormErrors = false;
        this.unitAreaMeasurementForm.markAsPristine();
        this.unitAreaMeasurementForm.markAsUntouched();
        this.unitAreaMeasurementForm.updateValueAndValidity();
    }

    /** ACTIONS */

    /**
     * On Submit
     */
    onSubmit(withBack: boolean = false) {
        this.hasFormErrors = false;
        const controls = this.unitAreaMeasurementForm.controls;
        /** check form */
        if (this.unitAreaMeasurementForm.invalid) {
            Object.keys(controls).forEach(controlName =>
                controls[controlName].markAsTouched()
            );

            this.hasFormErrors = true;
            return;
        }

        if (this.model.id > 0) {
            this.updateUnitAreaMeasurement(this.model);
        } else {
            this.createUnitAreaMeasurement(this.model);
        }
    }

    /**
     * Update model
     *
     * @param _unitAreaMeasurement: UnitAreaMeasurementModel
     */
    updateUnitAreaMeasurement(_unitAreaMeasurement: UnitAreaMeasurement) {
        this.loadingAfterSubmit = true;
        this.viewLoading = true;

        const updateUnitAreaMeasurement: Update<UnitAreaMeasurement> = {
            id: _unitAreaMeasurement.id,
            changes: _unitAreaMeasurement
        };
        this.store.dispatch(new UnitAreaMeasurementUpdated({
            partialunitAreaMeasurement: updateUnitAreaMeasurement,
            unitAreaMeasurement: _unitAreaMeasurement
        }));

        this.navigateToParent();
    }

    /**
     * Create model
     *
     * @param _unitAreaMeasurement: UnitAreaMeasurementModel
     */
    createUnitAreaMeasurement(_unitAreaMeasurement: UnitAreaMeasurement) {
        this.store.dispatch(new UnitAreaMeasurementOnServerCreated({unitAreaMeasurement: _unitAreaMeasurement}));
        this.componentSubscriptions = this.store.pipe(
            select(selectLastCreatedUnitAreaMeasurementId),
            delay(1000), // Remove this line
        ).subscribe(res => {
            if (!res) {
                return;
            }
            this.navigateToParent();
        });
    }

    /** Alect Close event */
    onAlertClose($event) {
        this.hasFormErrors = false;
    }


    isFormValid() {
        return (this.model && this.model.name && this.model.name.length > 0
            && this.model.acronym && this.model.acronym.length > 0);
    }

    navigateToParent() {
        let url = '../';
        if (this.model.id > 0) {
            url = '../../';
        }
        this.router.navigate([url], {relativeTo: this.activatedRoute});
    }

    canDeactivate() {
        if (this.discard()) {
            if (window.confirm(this.translate.instant('GENERAL.MESSAGE.LOST_CHANGES'))) {
                return true;
            } else {
                // ---------work around angular bug--------- reference: https://github.com/angular/angular/issues/13586
                const currentUrlTree = this.router.createUrlTree([], this.activatedRoute.snapshot);
                const currentUrl = currentUrlTree.toString();
                this.location.go(currentUrl);
                // ---------work around end-----------------
                return false;
            }
        }
        return true;
    }

    discard() {
        if (this.model && this.oldModel) {
            return this.model.name != this.oldModel.name || this.model.acronym != this.oldModel.acronym;
        }
        return false;
    }

}
